import { useBillingCurrency } from "$global-providers/billing-provider/provider";
import { useConvertFromPenceFactory } from "$routes/_lib/use-convert-currency";
import useCurrencyFormat from "$routes/_lib/use-currency-format";
import { makeStyles, Typography } from "@material-ui/core";
import classnames from "classnames";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles((theme) => ({
  root: {
    fontSize: 11,
    borderCollapse: "collapse",
    width: "100%",
    "& td, & th": {
      padding: "4px 0",
      "&:not(:last-child)": {
        paddingRight: theme.spacing(1),
      },
    },
  },
  textLeft: {
    textAlign: "left",
    fontSize: 12,
  },
  textRight: {
    textAlign: "right",
    fontSize: 12,
  },
  headerLabel: {
    fontSize: 12,
    color: theme.palette.grey[700],
  },
  itemDivider: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
  header: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
  },
}));

const InvoiceItem = ({ item }) => {
  const {
    quantity,
    cost,
    priceListItem: { name, priceListItemType },
  } = item;
  const classes = useStyles();
  const penceToPounds = useConvertFromPenceFactory();

  return (
    <tr className={classnames(classes.row, classes.itemDivider)}>
      <td>
        <Typography
          className={classes.textLeft}
        >{`${priceListItemType} - ${name}`}</Typography>
      </td>
      <td>
        <Typography className={classes.textRight}>{quantity}</Typography>
      </td>
      <td>
        <Typography className={classes.textRight}>
          {penceToPounds(cost)}
        </Typography>
      </td>
    </tr>
  );
};

const InvoicePrintItems = ({ lineItems }) => {
  const classes = useStyles();
  const currencyDetails = useBillingCurrency();
  const currency = useCurrencyFormat(currencyDetails);
  const formattedCurrency = currency?.formatToParts(currency)[0].value;

  return (
    <table className={classes.root}>
      <thead className={classes.header}>
        <tr>
          <th>
            <Typography
              className={classnames(classes.textLeft, classes.headerLabel)}
            >
              <strong>
                <FormattedMessage
                  id="printableInvoice.lineItem.item"
                  defaultMessage="ITEM"
                />
              </strong>
            </Typography>
          </th>
          <th>
            <Typography
              className={classnames(classes.textRight, classes.headerLabel)}
            >
              <strong>
                <FormattedMessage
                  id="printableInvoice.lineItem.quantity"
                  defaultMessage="QTY"
                />
              </strong>
            </Typography>
          </th>
          <th>
            <Typography
              className={classnames(classes.textRight, classes.headerLabel)}
            >
              <strong>
                <FormattedMessage
                  id="printableInvoice.lineItem.price"
                  defaultMessage="PRICE"
                />
                {formattedCurrency && ` (${formattedCurrency})`}
              </strong>
            </Typography>
          </th>
        </tr>
      </thead>
      <tbody>
        {lineItems.map((lineItem) => (
          <InvoiceItem key={lineItem._id} item={lineItem} />
        ))}
      </tbody>
    </table>
  );
};

InvoiceItem.propTypes = {
  item: PropTypes.shape({
    quantity: PropTypes.number,
    cost: PropTypes.number,
    priceListItem: PropTypes.shape({
      name: PropTypes.string,
      priceListItemType: PropTypes.string,
    }),
  }).isRequired,
};

InvoicePrintItems.propTypes = {
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }),
  ).isRequired,
};

export default InvoicePrintItems;
